import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  TextField,
  IconButton,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Slide,
  useTheme,
  alpha,
  InputAdornment,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  TrendingUp as TrendingUpIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { TransitionProps } from '@mui/material/transitions';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

interface MobileSearchBarProps {
  open: boolean;
  onClose: () => void;
  onSearch: (query: string) => void;
}

const MobileSearchBar: React.FC<MobileSearchBarProps> = ({
  open,
  onClose,
  onSearch
}) => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // تحميل تاريخ البحث من localStorage
  useEffect(() => {
    const savedHistory = localStorage.getItem('searchHistory');
    if (savedHistory) {
      setSearchHistory(JSON.parse(savedHistory));
    }
  }, []);

  // التركيز على حقل البحث عند فتح الحوار
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  // اقتراحات البحث الشائعة
  const popularSearches = [
    'هواتف ذكية',
    'لابتوب',
    'سماعات',
    'ساعات ذكية',
    'كاميرات',
    'ألعاب',
    'ملابس',
    'أحذية'
  ];

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchQuery(value);

    // إنشاء اقتراحات بناءً على النص المدخل
    if (value.length > 0) {
      const filteredSuggestions = popularSearches.filter(item =>
        item.toLowerCase().includes(value.toLowerCase())
      );
      setSuggestions(filteredSuggestions);
    } else {
      setSuggestions([]);
    }
  };

  const handleSearch = (query: string = searchQuery) => {
    if (query.trim()) {
      // إضافة البحث إلى التاريخ
      const newHistory = [query, ...searchHistory.filter(item => item !== query)].slice(0, 10);
      setSearchHistory(newHistory);
      localStorage.setItem('searchHistory', JSON.stringify(newHistory));

      onSearch(query);
      onClose();
      setSearchQuery('');
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const clearSearchHistory = () => {
    setSearchHistory([]);
    localStorage.removeItem('searchHistory');
  };

  const removeFromHistory = (item: string) => {
    const newHistory = searchHistory.filter(historyItem => historyItem !== item);
    setSearchHistory(newHistory);
    localStorage.setItem('searchHistory', JSON.stringify(newHistory));
  };

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      className="mobile-search-bar"
      TransitionComponent={Transition}
      sx={{
        '& .MuiDialog-paper': {
          bgcolor: alpha(theme.palette.background.default, 0.95),
          backdropFilter: 'blur(20px)'
        }
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* شريط البحث */}
        <Box
          sx={{
            p: 2,
            bgcolor: alpha(theme.palette.background.paper, 0.9),
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            backdropFilter: 'blur(10px)'
          }}
        >
          <TextField
            ref={inputRef}
            fullWidth
            placeholder="ابحث عن المنتجات..."
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyPress={handleKeyPress}
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="primary" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {searchQuery && (
                    <IconButton
                      size="small"
                      onClick={() => setSearchQuery('')}
                      sx={{ mr: 1 }}
                    >
                      <ClearIcon />
                    </IconButton>
                  )}
                  <IconButton onClick={onClose}>
                    <CloseIcon />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                borderRadius: 3,
                bgcolor: alpha(theme.palette.background.paper, 0.8),
                '& .MuiOutlinedInput-notchedOutline': {
                  border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: `2px solid ${alpha(theme.palette.primary.main, 0.4)}`
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: `2px solid ${theme.palette.primary.main}`
                }
              }
            }}
          />
        </Box>

        {/* محتوى البحث */}
        <Box sx={{ p: 2 }}>
          {/* الاقتراحات */}
          {suggestions.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                اقتراحات البحث
              </Typography>
              <List dense>
                {suggestions.map((suggestion, index) => (
                  <ListItem
                    key={index}
                    button
                    onClick={() => handleSearch(suggestion)}
                    sx={{
                      borderRadius: 2,
                      mb: 0.5,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.08)
                      }
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          width: 32,
                          height: 32
                        }}
                      >
                        <SearchIcon fontSize="small" />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={suggestion} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* تاريخ البحث */}
          {searchHistory.length > 0 && suggestions.length === 0 && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  عمليات البحث الأخيرة
                </Typography>
                <IconButton size="small" onClick={clearSearchHistory}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Box>
              <List dense>
                {searchHistory.map((item, index) => (
                  <ListItem
                    key={index}
                    button
                    onClick={() => handleSearch(item)}
                    sx={{
                      borderRadius: 2,
                      mb: 0.5,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.grey[500], 0.08)
                      }
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          bgcolor: alpha(theme.palette.grey[500], 0.1),
                          color: theme.palette.grey[600],
                          width: 32,
                          height: 32
                        }}
                      >
                        <HistoryIcon fontSize="small" />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={item} />
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFromHistory(item);
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* البحث الشائع */}
          {searchQuery === '' && suggestions.length === 0 && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                البحث الشائع
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                {popularSearches.map((search, index) => (
                  <Chip
                    key={index}
                    label={search}
                    onClick={() => handleSearch(search)}
                    icon={<TrendingUpIcon />}
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        borderColor: theme.palette.primary.main
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default MobileSearchBar;
